import { ThemeEvents } from '@theme/events';

const STOREFRONT_API_VERSION = '2026-04';
const POLL_INTERVAL = 600;
const MAX_POLL_ATTEMPTS = 15;

class ProductShippingCalculator extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-shipping-postal-code]'));
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-shipping-calculate]'));
    this.location = /** @type {HTMLElement | null} */ (this.querySelector('[data-shipping-location]'));
    this.results = /** @type {HTMLElement | null} */ (this.querySelector('[data-shipping-results]'));
    this.productForm = /** @type {HTMLFormElement | null} */ (this.closest('form'));
    this.productComponent = this.closest('product-form-component');

    if (!this.input || !this.button || !this.results || !this.productForm) return;

    this.button.addEventListener('click', this.calculate);
    this.input.addEventListener('input', this.formatPostalCode);
    this.input.addEventListener('keydown', this.handleKeydown);
    this.productComponent?.addEventListener(ThemeEvents.variantUpdate, this.clearResults);
    this.productComponent?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.clearResults);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.calculate);
    this.input?.removeEventListener('input', this.formatPostalCode);
    this.input?.removeEventListener('keydown', this.handleKeydown);
    this.productComponent?.removeEventListener(ThemeEvents.variantUpdate, this.clearResults);
    this.productComponent?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.clearResults);
  }

  clearResults = () => {
    if (!this.results) return;
    this.results.hidden = true;
    this.results.replaceChildren();
  };

  clearLocation = () => {
    if (!this.location) return;
    this.location.hidden = true;
    this.location.textContent = '';
  };

  formatPostalCode = () => {
    if (!this.input) return;

    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    this.input.removeAttribute('aria-invalid');
    this.clearLocation();
    this.clearResults();
  };

  /** @param {KeyboardEvent} event */
  handleKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.calculate();
  };

  calculate = async () => {
    if (!this.input || !this.button || !this.results || !this.productForm) return;

    const postalCode = this.input.value.replace(/\D/g, '');
    if (postalCode.length !== 8) {
      this.input.setAttribute('aria-invalid', 'true');
      this.showMessage('Digite um CEP válido com 8 números.', 'error');
      this.input.focus();
      return;
    }

    const variantInput = /** @type {HTMLInputElement | null} */ (this.productForm.querySelector('input[name="id"]'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.productForm.querySelector('input[name="quantity"]')
    );
    const variantId = Number(variantInput?.value);
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);

    if (!variantId) {
      this.showMessage('Selecione uma opção do produto antes de calcular o frete.', 'error');
      return;
    }

    this.setLoading(true);
    this.showMessage('Consultando as opções para este produto…', 'loading');

    try {
      const address = await this.lookupPostalCode(postalCode);
      this.showLocation(address);

      let rates = await this.fetchIsolatedShippingRates(variantId, quantity, address);
      let includesCartItems = false;
      const isolatedQuoteHasDeadline =
        rates.length > 0 &&
        rates.every((/** @type {object} */ rate) => this.extractShippingDays(rate, false));

      if (!isolatedQuoteHasDeadline) {
        try {
          const cartRates = await this.fetchCartShippingRates(postalCode, address);
          if (cartRates.length) {
            rates = cartRates;
            includesCartItems = true;
          }
        } catch (cartQuoteError) {
          console.warn('Prazo detalhado do carrinho indisponível; usando o prazo padrão.', cartQuoteError);
        }
      }

      this.renderRates(rates, quantity, includesCartItems);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível calcular o frete agora. Tente novamente em instantes.';
      this.showMessage(message, 'error');
    } finally {
      this.setLoading(false);
    }
  };

  /** @param {string} postalCode */
  async lookupPostalCode(postalCode) {
    const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
      headers: { Accept: 'application/json' },
    });
    const address = await this.parseResponse(response);

    if (address?.erro || !address?.localidade || !(address?.estado || address?.uf)) {
      throw new Error('CEP não encontrado. Confira os números e tente novamente.');
    }

    return address;
  }

  /**
   * Creates a temporary Storefront API cart so the quote contains only the
   * selected product and quantity, without touching the buyer's real cart.
   * @param {number} variantId
   * @param {number} quantity
   * @param {object} address
   */
  async fetchIsolatedShippingRates(variantId, quantity, address) {
    const mutation = `
      mutation ProductShippingCartCreate($input: CartInput) {
        cartCreate(input: $input) {
          cart { id }
          userErrors { field message }
        }
      }
    `;
    const variables = {
      input: {
        lines: [
          {
            merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
            quantity,
          },
        ],
        buyerIdentity: {
          countryCode: 'BR',
        },
        delivery: {
          addresses: [
            {
              address: {
                deliveryAddress: {
                  city: address.localidade,
                  provinceCode: address.uf,
                  zip: address.cep?.replace(/\D/g, ''),
                  countryCode: 'BR',
                },
              },
              selected: true,
              oneTimeUse: true,
              validationStrategy: 'COUNTRY_CODE_ONLY',
            },
          ],
        },
      },
    };
    const cartData = await this.storefrontRequest(mutation, variables);
    const userError = cartData?.cartCreate?.userErrors?.[0];

    if (userError) throw new Error(userError.message);

    const cartId = cartData?.cartCreate?.cart?.id;
    if (!cartId) throw new Error('Não foi possível preparar a cotação isolada.');

    const query = `
      query ProductShippingRates($cartId: ID!) {
        cart(id: $cartId) {
          ...ProductDeliveryGroups @defer
        }
      }

      fragment ProductDeliveryGroups on Cart {
        deliveryGroups(first: 10, withCarrierRates: true) {
          edges {
            node {
              deliveryOptions {
                title
                code
                description
                handle
                deliveryMethodType
                estimatedCost {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;
    const deliveryGroups = await this.storefrontDeferredRequest(query, { cartId });
    const options =
      deliveryGroups?.edges?.flatMap((/** @type {object} */ edge) => edge.node?.deliveryOptions || []) || [];

    return options.map((/** @type {object} */ option) => ({
      name: option.title || option.code || 'Entrega',
      description: option.description,
      handle: option.handle,
      price: option.estimatedCost?.amount,
      currency: option.estimatedCost?.currencyCode,
      delivery_method_type: option.deliveryMethodType,
      source: 'storefront',
    }));
  }

  /**
   * Reads the current cart only when the isolated carrier response omits its
   * deadline. Preparing rates doesn't add, remove or update cart items.
   * @param {string} postalCode
   * @param {object} address
   */
  async fetchCartShippingRates(postalCode, address) {
    const cartUrl = Theme.routes.cart_url.replace(/\/$/, '');
    const cart = await this.fetchJson(`${cartUrl}.js`);
    const itemCount = Number(cart?.item_count) || cart?.items?.length || 0;

    if (itemCount < 1) return [];

    const params = new URLSearchParams({
      'shipping_address[zip]': postalCode,
      'shipping_address[country]': this.dataset.country || 'Brazil',
      'shipping_address[province]': address.estado || address.uf,
      'shipping_address[city]': address.localidade,
    });
    const prepareUrl = `${cartUrl}/prepare_shipping_rates.json?${params}`;
    const asyncUrl = `${cartUrl}/async_shipping_rates.json?${params}`;

    await this.fetchJson(prepareUrl, { method: 'POST' });

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      if (attempt > 0) await this.wait(POLL_INTERVAL);

      const response = await fetch(asyncUrl, { headers: { Accept: 'application/json' } });
      const data = await this.parseResponse(response);
      if (data?.shipping_rates) return data.shipping_rates;
    }

    return [];
  }

  /** @param {object} address */
  showLocation(address) {
    if (!this.location) return;
    this.location.textContent = `${address.localidade} — ${address.uf}`;
    this.location.hidden = false;
  }

  /**
   * @param {Array<object>} rates
   * @param {number} quantity
   * @param {boolean} includesCartItems
   */
  renderRates(rates, quantity, includesCartItems = false) {
    if (!this.results) return;

    this.results.replaceChildren();
    this.results.dataset.state = 'success';
    this.results.hidden = false;

    if (!rates.length) {
      this.showMessage('Não encontramos opções de entrega para este CEP.', 'empty');
      return;
    }

    const list = document.createElement('ul');
    list.className = 'product-shipping-calculator__rate-list';

    rates.forEach((rate, index) => {
      const item = document.createElement('li');
      item.className = 'product-shipping-calculator__rate';

      const details = document.createElement('span');
      details.className = 'product-shipping-calculator__rate-details';

      const name = document.createElement('strong');
      name.textContent = rate.presentment_name || rate.name || 'Entrega';
      details.append(name);

      this.formatDeliveryDetails(rate).forEach((detail) => {
        const detailElement = document.createElement('small');
        detailElement.textContent = detail;
        details.append(detailElement);
      });

      if (index === 0) {
        const note = document.createElement('small');
        note.className = 'product-shipping-calculator__cart-note';
        note.textContent = `Cotação para ${quantity} unidade${quantity === 1 ? '' : 's'} deste produto${
          includesCartItems ? ', considerando também os itens do carrinho' : ''
        }.`;
        details.append(note);
      }

      const price = document.createElement('strong');
      price.className = 'product-shipping-calculator__rate-price';
      price.textContent = 'Grátis';
      price.dataset.freeShipping = 'true';

      item.append(details, price);
      list.append(item);
    });

    this.results.append(list);
  }

  /** @param {object} rate */
  formatDeliveryDetails(rate) {
    const details = [];
    const shippingDays = this.extractShippingDays(rate);
    const manufacturingDays = Number(this.dataset.manufacturingDays) || 0;

    if (shippingDays) {
      const shippingLabel =
        shippingDays.minimum === 0
          ? `até ${shippingDays.maximum} dias úteis de envio`
          : shippingDays.minimum === shippingDays.maximum
            ? `${shippingDays.minimum} dias úteis de envio`
            : `${shippingDays.minimum} a ${shippingDays.maximum} dias úteis de envio`;

      if (manufacturingDays > 0) {
        details.push(`${manufacturingDays} dias de fabricação + ${shippingLabel}`);
      } else {
        details.push(shippingLabel);
      }

      return details;
    }

    if (rate.description) details.push(rate.description);

    if (Array.isArray(rate.delivery_range) && rate.delivery_range.length === 2) {
      details.push(
        `Entrega estimada entre ${this.formatDate(rate.delivery_range[0])} e ${this.formatDate(
          rate.delivery_range[1]
        )}`
      );
    } else if (rate.delivery_date) {
      details.push(`Entrega estimada em ${this.formatDate(rate.delivery_date)}`);
    }

    if (manufacturingDays > 0) details.push(`${manufacturingDays} dias de fabricação`);
    return [...new Set(details)];
  }

  /**
   * @param {object} rate
   * @param {boolean} useFallback
   */
  extractShippingDays(rate, useFallback = true) {
    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
      const minimum = Number(rate.delivery_days[0]);
      const maximum = Number(rate.delivery_days[1] ?? minimum);
      if (Number.isFinite(minimum) && Number.isFinite(maximum) && minimum >= 0 && maximum > 0) {
        return { minimum, maximum };
      }
    }

    const directDays = Number(rate.delivery_days);
    if (Number.isFinite(directDays) && directDays > 0) {
      return { minimum: directDays, maximum: directDays };
    }

    const text = [rate.description, rate.name, rate.presentment_name, rate.code, rate.handle]
      .filter(Boolean)
      .join(' ');
    const rangeMatch = text.match(/(\d+)\s*(?:a|até|-)\s*(\d+)\s*dias?/i);
    if (rangeMatch) {
      return { minimum: Number(rangeMatch[1]), maximum: Number(rangeMatch[2]) };
    }

    const singleMatch = text.match(/(\d+)\s*dias?/i);
    if (singleMatch) {
      const days = Number(singleMatch[1]);
      return { minimum: days, maximum: days };
    }

    if (useFallback) {
      const fallbackDays = Number(this.dataset.shippingDaysFallback);
      if (Number.isFinite(fallbackDays) && fallbackDays > 0) {
        return { minimum: fallbackDays, maximum: fallbackDays };
      }
    }

    return null;
  }

  /** @param {string} value */
  formatDate(value) {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(this.dataset.locale || 'pt-BR', {
      day: '2-digit',
      month: 'short',
    }).format(date);
  }

  /**
   * @param {string|number} value
   * @param {string|null} rateCurrency
   */
  formatMoney(value, rateCurrency) {
    const currency = rateCurrency || this.dataset.currency || Shopify.currency?.active || 'BRL';
    const amount = Number(value);
    if (!Number.isFinite(amount)) return String(value);

    return new Intl.NumberFormat(this.dataset.locale || 'pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  /**
   * @param {string} query
   * @param {object} variables
   */
  async storefrontRequest(query, variables) {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });
    const payload = await this.parseResponse(response);

    if (payload?.errors?.length) throw new Error(payload.errors[0].message);
    return payload?.data;
  }

  /**
   * @param {string} query
   * @param {object} variables
   */
  async storefrontDeferredRequest(query, variables) {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        Accept: 'multipart/mixed; deferSpec=20220824, application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });
    const text = await response.text();

    if (!response.ok) throw new Error('Não foi possível consultar as transportadoras.');

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('multipart/mixed')) {
      const payload = JSON.parse(text);
      if (payload?.errors?.length) throw new Error(payload.errors[0].message);
      return payload?.data?.cart?.deliveryGroups || payload?.incremental?.[0]?.data?.deliveryGroups;
    }

    const boundary = contentType.match(/boundary="?([^";]+)"?/i)?.[1] || 'graphql';
    const parts = text.split(`--${boundary}`);

    for (const part of parts) {
      const jsonStart = part.indexOf('{');
      if (jsonStart === -1) continue;

      try {
        const payload = JSON.parse(part.slice(jsonStart).trim());
        const deliveryGroups =
          payload?.data?.cart?.deliveryGroups || payload?.incremental?.[0]?.data?.deliveryGroups;
        if (deliveryGroups) return deliveryGroups;
      } catch (_error) {
        // Multipart closing boundaries and headers aren't JSON payloads.
      }
    }

    throw new Error('As transportadoras não retornaram opções para este CEP.');
  }

  /**
   * @param {string} message
   * @param {'error'|'loading'|'empty'} state
   */
  showMessage(message, state) {
    if (!this.results) return;

    const paragraph = document.createElement('p');
    paragraph.className = 'product-shipping-calculator__message';
    paragraph.textContent = message;

    this.results.replaceChildren(paragraph);
    this.results.dataset.state = state;
    this.results.hidden = false;
  }

  /** @param {boolean} loading */
  setLoading(loading) {
    if (!this.button || !this.input) return;

    this.button.disabled = loading;
    this.input.disabled = loading;
    this.button.textContent = loading ? 'Calculando…' : 'Calcular';
    this.setAttribute('aria-busy', String(loading));
  }

  /**
   * @param {string} url
   * @param {RequestInit} [options]
   */
  async fetchJson(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });
    return this.parseResponse(response);
  }

  /** @param {Response} response */
  async parseResponse(response) {
    const text = await response.text();
    let data = null;

    if (text) {
      try {
        data = JSON.parse(text);
      } catch (_error) {
        data = null;
      }
    }

    if (!response.ok) {
      const serverMessage =
        data?.description ||
        data?.message ||
        data?.errors?.[0]?.message ||
        (data && typeof data === 'object' ? Object.values(data).flat().filter(Boolean)[0] : null);
      throw new Error(
        typeof serverMessage === 'string'
          ? serverMessage
          : 'Não foi possível calcular o frete agora. Tente novamente em instantes.'
      );
    }

    return data;
  }

  /** @param {number} milliseconds */
  wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

}

if (!customElements.get('product-shipping-calculator')) {
  customElements.define('product-shipping-calculator', ProductShippingCalculator);
}
